import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUpload from './ImageUpload';
import './PortfolioForm.css';

const PortfolioForm = ({ portfolio, onSave, onCancel, categories }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    category: 'siteweb',
    date: new Date().getFullYear().toString(),
    image: '',
    gallery: [''],
    technologies: [''],
    client: ''
  });

  useEffect(() => {
    if (portfolio) {
      setFormData({
        ...portfolio,
        gallery: portfolio.gallery || [''],
        technologies: portfolio.technologies || [''],
        client: portfolio.client || ''
      });
    }
  }, [portfolio]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleArrayChange = (index, value, arrayName) => {
    const newArray = [...formData[arrayName]];
    newArray[index] = value;
    setFormData({
      ...formData,
      [arrayName]: newArray
    });
  };

  const addArrayItem = (arrayName) => {
    setFormData({
      ...formData,
      [arrayName]: [...formData[arrayName], '']
    });
  };

  const removeArrayItem = (index, arrayName) => {
    const newArray = formData[arrayName].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [arrayName]: newArray
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="portfolio-form-container">
      <div className="portfolio-form">
        <div className="form-header">
          <h3>
            {portfolio 
              ? t('admin.portfolio.edit') 
              : t('admin.portfolio.add')
            }
          </h3>
          <button 
            onClick={onCancel}
            className="close-button"
            type="button"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">{t('admin.portfolio.fields.name')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">{t('admin.portfolio.fields.description')}</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="url">{t('admin.portfolio.fields.url')}</label>
            <input
              type="url"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleChange}
              placeholder="https://example.com"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">{t('admin.portfolio.fields.category')}</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="date">{t('admin.portfolio.fields.date')}</label>
              <input
                type="text"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                placeholder="2023"
                required
              />
            </div>
          </div>

          <div className="form-group form-group-full">
            <ImageUpload
              label="Image mise en avant"
              currentImage={formData.image}
              onImageUpload={(dataUrl) => setFormData({...formData, image: dataUrl})}
              onImageDelete={() => setFormData({...formData, image: ''})}
            />
          </div>

          <div className="form-group">
            <label htmlFor="client">Client</label>
            <input
              type="text"
              id="client"
              name="client"
              value={formData.client}
              onChange={handleChange}
              placeholder="Nom du client"
            />
          </div>

          <div className="form-group form-group-full">
            <label>Galerie d'images</label>
            <div className="gallery-uploads">
              {formData.gallery.map((img, index) => (
                <div key={index} className="gallery-item">
                  <ImageUpload
                    label={`Image ${index + 1}`}
                    currentImage={img}
                    onImageUpload={(dataUrl) => handleArrayChange(index, dataUrl, 'gallery')}
                    onImageDelete={() => removeArrayItem(index, 'gallery')}
                  />
                </div>
              ))}
              <button 
                type="button"
                onClick={() => addArrayItem('gallery')}
                className="add-gallery-btn"
              >
                + Ajouter une image à la galerie
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Technologies utilisées</label>
            {formData.technologies.map((tech, index) => (
              <div key={index} className="array-item">
                <input
                  type="text"
                  value={tech}
                  onChange={(e) => handleArrayChange(index, e.target.value, 'technologies')}
                  placeholder="React, Node.js, etc."
                />
                <button 
                  type="button"
                  onClick={() => removeArrayItem(index, 'technologies')}
                  className="remove-btn"
                  disabled={formData.technologies.length <= 1}
                >
                  ×
                </button>
              </div>
            ))}
            <button 
              type="button"
              onClick={() => addArrayItem('technologies')}
              className="add-btn"
            >
              + Ajouter une technologie
            </button>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              onClick={onCancel}
              className="cancel-button"
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit"
              className="save-button"
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PortfolioForm;
